/**
 * Phase 125 — Image uploader for ArenaX host registration flows.
 *
 * Supports:
 *   - Single-image mode (cover image, logo) — replace-on-upload.
 *   - Multi-image mode (gallery) — append, reorder via display_order,
 *     delete via soft-remove from the local array.
 *
 * Uploads stream through `POST /api/arenax/host/upload` (Emergent Object
 * Storage backed) and store the returned `path` + `url` for display.
 */
import { useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ImagePlus, Loader2, Trash2, GripVertical, Star } from 'lucide-react';

function resolveSrc(image) {
  // Stored as { url, path }; url is API-relative
  if (!image) return null;
  if (typeof image === 'string') return image;
  if (image.url?.startsWith('http')) return image.url;
  if (image.url?.startsWith('/api/')) {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return base + image.url;
  }
  return image.url || image.path || null;
}

export function ArenaxImageUploader({
  label, kind = 'cover', value, onChange, helper, testId,
}) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post(`/arenax/host/upload?kind=${kind}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange?.(r.data); // {path, url, content_type, size}
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-1.5" data-testid={testId}>
      {label && (
        <label className="text-[11px] tracking-wider uppercase font-bold block"
               style={{ color: 'hsl(var(--ax-text-muted))' }}>
          {label}
        </label>
      )}
      <div
        onClick={onPick}
        className="relative cursor-pointer rounded-xl border-2 border-dashed overflow-hidden transition-colors"
        style={{
          borderColor: value ? 'transparent' : 'var(--ax-border-bold)',
          background: value ? 'transparent' : 'var(--ax-card-bg-soft)',
          minHeight: 120,
        }}
      >
        {value ? (
          <img
            src={resolveSrc(value)}
            alt={kind}
            className="w-full h-32 object-cover"
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin"
                       style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
            ) : (
              <ImagePlus className="h-6 w-6 mb-1.5"
                         style={{ color: 'hsl(var(--ax-text-faint))' }} />
            )}
            <div className="text-xs font-semibold"
                 style={{ color: 'hsl(var(--ax-text-muted))' }}>
              {uploading ? 'Uploading…' : 'Click to upload image'}
            </div>
            <div className="text-[10px] mt-0.5"
                 style={{ color: 'hsl(var(--ax-text-faint))' }}>
              PNG / JPG · max 5MB
            </div>
          </div>
        )}
        {value && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange?.(null); }}
            className="absolute top-2 right-2 h-7 w-7 rounded-full flex items-center justify-center transition-colors"
            style={{ background: 'rgba(0,0,0,0.55)', color: '#fff' }}
            title="Remove"
            data-testid={`${testId}-remove`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFile}
        />
      </div>
      {helper && (
        <p className="text-[11px]" style={{ color: 'hsl(var(--ax-text-faint))' }}>{helper}</p>
      )}
    </div>
  );
}

export function ArenaxGalleryUploader({ label, value = [], onChange, max = 8, helper, testId }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const setItems = (items) => {
    // Re-stamp display_order so 0 = first
    const re = items.map((it, i) => ({ ...it, display_order: i }));
    onChange?.(re);
  };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = Math.max(0, max - value.length);
    if (remaining === 0) {
      toast.error(`Gallery max is ${max} images`);
      return;
    }
    const slice = files.slice(0, remaining);
    setUploading(true);
    try {
      for (const file of slice) {
        const fd = new FormData();
        fd.append('file', file);
        const r = await api.post('/arenax/host/upload?kind=gallery', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        value = [...value, { ...r.data, display_order: value.length }];
      }
      setItems(value);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = (idx) => setItems(value.filter((_, i) => i !== idx));
  const makeFirst = (idx) => {
    const next = [...value];
    const [it] = next.splice(idx, 1);
    next.unshift(it);
    setItems(next);
  };
  const move = (idx, delta) => {
    const next = [...value];
    const j = idx + delta;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
  };

  return (
    <div className="space-y-1.5" data-testid={testId}>
      {label && (
        <label className="text-[11px] tracking-wider uppercase font-bold block"
               style={{ color: 'hsl(var(--ax-text-muted))' }}>
          {label}
        </label>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {value.map((img, i) => (
          <div
            key={img.path || img.url || i}
            className="relative aspect-square rounded-lg overflow-hidden border"
            style={{ borderColor: i === 0 ? 'rgba(16,185,129,0.40)' : 'var(--ax-border-quiet)' }}
            data-testid={`${testId}-item-${i}`}
          >
            <img src={resolveSrc(img)} alt={`gallery-${i}`} className="w-full h-full object-cover" />
            {i === 0 && (
              <div className="absolute top-1.5 left-1.5 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full"
                   style={{ background: 'rgba(16,185,129,0.95)', color: '#fff' }}>
                <Star className="h-2.5 w-2.5 inline mr-0.5" /> Hero
              </div>
            )}
            <div className="absolute bottom-0 inset-x-0 flex items-center justify-between px-1 py-1 bg-black/55">
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="text-white/90 text-[10px] disabled:opacity-30 px-1"
                title="Move left"
              ><GripVertical className="h-3 w-3 inline" /> ↑</button>
              {i !== 0 && (
                <button
                  type="button"
                  onClick={() => makeFirst(i)}
                  className="text-white/90 text-[10px] px-1"
                  title="Set as hero"
                >Hero</button>
              )}
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-white/90 text-[10px] px-1"
                title="Remove"
              ><Trash2 className="h-3 w-3" /></button>
            </div>
          </div>
        ))}
        {value.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center transition-colors"
            style={{
              borderColor: 'var(--ax-border-bold)',
              background: 'var(--ax-card-bg-soft)',
            }}
            data-testid={`${testId}-add`}
          >
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
            ) : (
              <>
                <ImagePlus className="h-5 w-5 mb-1" style={{ color: 'hsl(var(--ax-text-faint))' }} />
                <span className="text-[10px] font-semibold" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                  Add image
                </span>
              </>
            )}
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={onFile}
        />
      </div>
      {helper && (
        <p className="text-[11px]" style={{ color: 'hsl(var(--ax-text-faint))' }}>
          {helper} {value.length > 0 && <span className="ml-1">· {value.length}/{max}</span>}
        </p>
      )}
    </div>
  );
}

export { resolveSrc };
